import { useState, useEffect, useRef, useCallback } from "react";
import { searchAPI } from "../services/api";

// Shared site-search hook powering the header dropdown (and reusable elsewhere).
//
// It handles the three things a search-as-you-type box needs beyond a plain
// fetch:
//   1. Debounce  — waits `debounceMs` after the last keystroke before calling
//                  the API, so "manufacturing" is one request, not thirteen.
//   2. Min length — doesn't hit the API until the query is at least `minChars`.
//   3. Stale-guard — cancels the previous request (AbortController) and ignores
//                  any response that isn't the most recent (latest-wins), so the
//                  dropdown never flickers back to results for an older query.
export default function useSiteSearch({
  debounceMs = 250,
  minChars = 2,
  limit,
} = {}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const abortRef = useRef(null);
  const reqIdRef = useRef(0);

  const reset = useCallback(() => {
    setResults([]);
    setTotal(0);
    setSuggestion(null);
    setLoading(false);
    setError(false);
  }, []);

  const runSearch = useCallback(
    async (raw) => {
      const q = (raw || "").trim();

      // Cancel whatever is in flight before starting/skipping.
      if (abortRef.current) abortRef.current.abort();

      if (q.length < minChars) {
        reqIdRef.current++; // invalidate any pending response
        reset();
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const reqId = ++reqIdRef.current;

      setLoading(true);
      setError(false);
      try {
        const res = await searchAPI.search(q, { limit, signal: controller.signal });
        if (reqId !== reqIdRef.current) return; // a newer request superseded us
        setResults(res.data.results || []);
        setTotal(res.data.total || 0);
        setSuggestion(res.data.suggestion || null);
        setLoading(false);
      } catch (err) {
        // Aborted requests are expected — ignore them silently.
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        if (reqId !== reqIdRef.current) return;
        reset();
        setError(true);
      }
    },
    [minChars, limit, reset]
  );

  // Debounce: re-run the search a beat after `query` settles.
  useEffect(() => {
    const id = setTimeout(() => runSearch(query), debounceMs);
    return () => clearTimeout(id);
  }, [query, debounceMs, runSearch]);

  // Abort any in-flight request on unmount.
  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  return { query, setQuery, results, total, suggestion, loading, error, reset };
}
